import ButtonGroup from 'components/ButtonGroup';
import Button from 'components/Button';
import Spacing from 'components/Spacing';
import HoldingClassRightsForm from 'routes/LpPages/Common/HoldingClass/HoldingClassRightsForm';
import { useFormContext } from 'react-hook-form';
import ClassHoldingsList from './ClassHoldingsList';
import { Grid } from '@mui/material';
import api from 'lib/trpcClient';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import PageSection from 'components/PageSection';
import ErrorBoundary from 'components/ErrorBoundary';
import InvestmentHistory from './InvestmentHistory';
import Heading from 'components/Heading';
import { CurrencyCode } from 'further-types/lp/vehicle';

type Props = {
  newHoldingClasses: boolean;
  vehicleId: string;
  companyId: string;
  onRevalueClick: () => void;
  setNewHoldingClasses: (newHoldingClasses: boolean) => void;
  vehicleCurrencyCode: CurrencyCode;
};

const HoldingsOverview: React.FC<Props> = ({
  newHoldingClasses,
  vehicleId,
  companyId,
  onRevalueClick,
  setNewHoldingClasses,
  vehicleCurrencyCode,
}: Props) => {
  const { watch, getValues, setValue, clearErrors, trigger, control } =
    useFormContext();

  const holdingClasses = watch('holdingClasses');

  const mutationOpts = useHandleTrpcMutation();
  const { mutateAsync: createHoldingClass, isPending: isCreatingHoldingClass } =
    api.holdingClass.create.useMutation(
      mutationOpts({
        successMessage: 'Holding class created successfully',
      }),
    );

  const { mutateAsync: deleteHoldingClass } =
    api.holdingClass.remove.useMutation(
      mutationOpts({
        successMessage: 'Holding class deleted successfully.',
      }),
    );

  const { mutateAsync: updateHoldingClass, isPending: isUpdatingHoldingClass } =
    api.holdingClass.update.useMutation(
      mutationOpts({
        successMessage: 'Holding class updated successfully',
      }),
    );

  const onSaveHoldingClass = async () => {
    try {
      const holdingClass = getValues('newHoldingClasses');

      const firstHoldingClass = holdingClass[0];

      const isValid = await trigger(['newHoldingClasses.0.name']);
      if (!isValid) return;

      const newHoldingClasses = await createHoldingClass({
        assetId: companyId,
        vehicleId,
        holdingClass: firstHoldingClass,
      });

      if (newHoldingClasses) {
        setValue('holdingClasses', [newHoldingClasses, ...holdingClasses]);

        clearErrors('newHoldingClasses');

        setValue('newHoldingClasses', [
          {
            name: '',
            assetId: undefined,
            currency: getValues('baseCurrency'),
            monitoringRights: undefined,
            liquidationPreferenceType: undefined,
            liquidationPreferenceMultiple: undefined,
            antiDilutionRights: undefined,
            couponRights: undefined,
            couponAmount: undefined,
          },
        ]);

        setNewHoldingClasses(false);
      }
    } catch (error) {
      console.error('Failed to save holding class:', error);
    }
  };

  const onUpdateHoldingClass = async (holdingClassId?: string) => {
    try {
      if (!holdingClassId) {
        throw new Error('Holding class ID is required.');
      }

      const holdingClasses = getValues('holdingClasses');

      const holdingClass = holdingClasses.find(
        (hc) => hc._id === holdingClassId,
      );

      if (!holdingClass) {
        throw new Error('Holding class not found.');
      }

      const updatedHoldingClass = await updateHoldingClass({
        vehicleId,
        holdingClass,
      });

      if (updatedHoldingClass) {
        setValue(
          'holdingClasses',
          holdingClasses.map((hc) =>
            hc._id === holdingClassId ? updatedHoldingClass : hc,
          ),
        );
      }
    } catch (error) {
      console.error('Failed to update holding class:', error);
    }
  };

  const onDeleteHoldingClass = async (holdingClassId: string) => {
    try {
      const deletedHoldingClassId = await deleteHoldingClass({
        id: holdingClassId,
        assetId: companyId,
        vehicleId,
      });

      if (deletedHoldingClassId) {
        setValue(
          'holdingClasses',
          holdingClasses.filter((hc) => hc._id !== deletedHoldingClassId),
        );
      }
    } catch (error) {
      console.error('Failed to delete holding class:', error);
    }
  };

  return (
    <Spacing>
      {newHoldingClasses && (
        <>
          <Grid container>
            <Grid size={{ xs: 8 }}>
              <Heading variant="h2">Class rights</Heading>
              <HoldingClassRightsForm
                index={0}
                formName="newHoldingClasses"
                control={control}
                vehicleCurrencyCode={vehicleCurrencyCode}
              />
            </Grid>
          </Grid>
          <Spacing>
            <ButtonGroup>
              <Button
                variant="contained"
                color="primary"
                onClick={onSaveHoldingClass}
                loading={isCreatingHoldingClass}
              >
                Save new holding class
              </Button>
            </ButtonGroup>
          </Spacing>
        </>
      )}
      <Grid container>
        <Grid size={{ xs: 12 }}>
          <ErrorBoundary>
            <ClassHoldingsList
              holdingClasses={holdingClasses}
              onDeleteHoldingClass={onDeleteHoldingClass}
              onUpdateHoldingClass={onUpdateHoldingClass}
              isUpdatingHoldingClass={isUpdatingHoldingClass}
              companyId={companyId}
              vehicleId={vehicleId}
              onRevalueClick={onRevalueClick}
              vehicleCurrencyCode={vehicleCurrencyCode}
            />
          </ErrorBoundary>
        </Grid>
      </Grid>
      {companyId && (
        <PageSection title="Investment history">
          <ErrorBoundary>
            <InvestmentHistory vehicleId={vehicleId} companyId={companyId} />
          </ErrorBoundary>
        </PageSection>
      )}
    </Spacing>
  );
};

export default HoldingsOverview;
